import { useCallback } from 'react';
import { CanceledError } from 'axios';
import { isFunction, noop } from 'lodash';
// Import Types
import { ResponseModel } from 'types';
// Import Hooks
import useToaster from './UseToaster';
// Import Constants
import { AlertTypes } from 'constants/common';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestFnType = (...args: any[]) => Promise<ResponseModel<any>>;

const useRequest = () => {
    const { showToaster } = useToaster();

    const doRequest = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (requestFn?: RequestFnType, params?: any, args?: any[]): Promise<any> => {
            if (!isFunction(requestFn)) {
                const errorMessage = `${requestFn} should be a function`;
                console.error(errorMessage);
                return Promise.reject(new Error(errorMessage));
            } else {
                const {
                    successCallback = noop,
                    errorCallback = noop,
                    catchCallback = noop,
                    isShowError = true,
                } = params;

                return new Promise((resolve) => {
                    const resolveResponseObject = (response) => {
                        const { HasError, AlertMessage, Data } = response;

                        if (!HasError) {
                            successCallback(Data, AlertMessage);
                        } else {
                            isShowError && showToaster(AlertTypes.error, AlertMessage);
                            errorCallback(AlertMessage);
                        }

                        resolve(response);
                    };

                    const getErrorMessage = (error) => {
                        if (!error) return 'Unknown error';
                        // Check for custom cancellation reason
                        if (error.code === 'ERR_CANCELED' && error.config?.signal?.reason) {
                            return `[Request Cancelled] ${error.config.signal.reason}`;
                        }
                        if (error.message) return error.message;
                        if (error.response?.data?.message) return error.response.data.message;
                    };

                    if (!requestFn) {
                        const errorMessage = 'Request function is not provided';
                        console.error(errorMessage);
                        return Promise.reject(new Error(errorMessage));
                    }

                    requestFn(...(args ?? []))
                        .then(
                            (response) => {
                                if (response instanceof Blob) {
                                    if (response.type !== 'application/json') {
                                        successCallback(response, null);
                                        resolve(response);
                                    } else {
                                        response.text().then((responseJSON) => {
                                            resolveResponseObject(JSON.parse(responseJSON));
                                        });
                                    }
                                } else {
                                    resolveResponseObject(response);
                                }
                            },
                            (error) => {
                                const message = getErrorMessage(error);
                                const isCanceled = error instanceof CanceledError;

                                // Cancellation is expected (unmount or a newer call superseded this one), not an error
                                if (!isCanceled) {
                                    console.error(message);
                                    showToaster(AlertTypes.error, message);
                                    errorCallback(message);
                                }
                                resolve({
                                    HasError: true,
                                    IsCanceled: isCanceled,
                                    AlertMessage: message,
                                    AlertType: AlertTypes.danger,
                                });
                            },
                        )
                        .catch((error) => {
                            // Please do not touch any state of app in catch block!
                            const message = getErrorMessage(error);
                            console.error(message);
                            catchCallback(error);
                        });
                });
            }
        },
        [showToaster],
    );

    const doPostRequest = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <T extends RequestFnType = any>(
            requestFn?: T,
            params?: {
                queryString?: Parameters<T>[1] extends undefined ? AnyObject : Parameters<T>[1];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                requestBody?: any;
                successCallback?: (data: Awaited<ReturnType<T>>['Data'], alertMessage?: string) => void;
                errorCallback?: (alertMessage?: string) => void;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                catchCallback?: (errorMessage?: any) => void;
                isShowError?: boolean;
            },
        ): Promise<Awaited<ReturnType<T>>> => {
            const { requestBody, queryString = {} } = params || {};
            const args = [requestBody, queryString];
            return doRequest(requestFn, params, args);
        },
        [doRequest],
    );

    const doGetRequest = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <T extends RequestFnType = any>(
            requestFn: T,
            params: {
                queryString?: Parameters<T>[0] extends undefined ? AnyObject : Parameters<T>[0];
                successCallback?: (data: Awaited<ReturnType<T>>['Data'], alertMessage?: string) => void;
                errorCallback?: (alertMessage?: string) => void;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                catchCallback?: (errorMessage?: any) => void;
                isShowError?: boolean;
            },
        ): Promise<Awaited<ReturnType<T>>> => {
            const { queryString = {} } = params;
            const args = [queryString];
            return doRequest(requestFn, params, args);
        },
        [doRequest],
    );

    const doDeleteRequest = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <T extends RequestFnType = any>(
            requestFn: T,
            params: {
                queryString?: Parameters<T>[0] extends undefined ? AnyObject : Parameters<T>[0];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                requestBody?: any;
                successCallback?: (data: Awaited<ReturnType<T>>['Data'], alertMessage?: string) => void;
                errorCallback?: (alertMessage?: string) => void;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                catchCallback?: (errorMessage?: any) => void;
                isShowError?: boolean;
            },
        ): Promise<Awaited<ReturnType<T>>> => {
            const { queryString = {} } = params;
            const args = [queryString];
            return doRequest(requestFn, params, args);
        },
        [doRequest],
    );

    return { doPostRequest, doGetRequest, doDeleteRequest };
};

export default useRequest;
